/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState } from "react";
import Layout from "@/components/Layout";
import { toast } from "react-toastify";
import {
  useCurrentPhaseStats,
  useAllPhases,
  useActiveNations,
  useEliminatedNations,
  useAdvancePhase,
  useSimulateAutoAdvance,
} from "@/hooks/api/usePhases";
import PhaseOverview from "@/pages/phases/PhaseOverview";
import PhaseTimeline from "@/pages/phases/PhaseTimeline";
import ActiveNations from "@/pages/phases/ActiveNations";
import EliminatedNations from "@/pages/phases/EliminatedNations";
import AdvancePhaseDialog from "@/pages/phases/AdvancePhaseDialog";
import type { Phase } from "@/types/api.types";

export default function PhaseManagement() {
  const [showAdvanceDialog, setShowAdvanceDialog] = useState(false);

  const { data: stats, isLoading: statsLoading } = useCurrentPhaseStats();
  const { data: phases } = useAllPhases();
  const { data: activeNations } = useActiveNations();
  const { data: eliminatedNations } = useEliminatedNations();
  const { mutate: advancePhase, isPending: isAdvancing } = useAdvancePhase();
  const { mutate: simulateAdvance, isPending: isSimulating } =
    useSimulateAutoAdvance();

  const handleAdvancePhase = (
    newPhase: Phase,
    eliminatedNationIds: number[],
  ) => {
    advancePhase(
      { newPhase, eliminatedNationIds },
      {
        onSuccess: (res: any) => {
          toast.success(res.message || "Phase advanced successfully!");
          setShowAdvanceDialog(false);
        },
        onError: (error: any) => {
          toast.error(error.response?.data?.message || "Failed to advance phase");
        },
      },
    );
  };

  const handleSimulateAdvance = () => {
    if (
      !confirm(
        "Auto-advance will eliminate all losing nations from completed matches. Continue?",
      )
    )
      return;

    simulateAdvance(undefined, {
      onSuccess: (res: any) => {
        toast.success(res.message || "Phase simulated successfully!");
      },
      onError: (error: any) =>
        toast.error(
          error.response?.data?.message || "Failed to simulate advance",
        ),
    });
  };

  if (statsLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Phase Management</h1>
        <p className="text-gray-500 mt-1">
          Control tournament progression and nation elimination
        </p>
      </div>

      <div className="space-y-6">
        {/* Current Phase Overview */}
        {stats && (
          <PhaseOverview
            stats={stats}
            onAdvance={() => setShowAdvanceDialog(true)}
            onSimulate={handleSimulateAdvance}
            isAdvancing={isAdvancing}
            isSimulating={isSimulating}
          />
        )}

        {/* Phase Timeline */}
        {phases && (
          <PhaseTimeline phases={phases} currentPhase={stats?.currentPhase} />
        )}

        <div className="grid grid-cols-2 gap-6">
          {/* Active Nations */}
          <ActiveNations nations={activeNations || []} />

          {/* Eliminated Nations */}
          <EliminatedNations grouped={eliminatedNations || {}} />
        </div>
      </div>

      {/* Advance Phase Dialog */}
      {stats && (
        <AdvancePhaseDialog
          open={showAdvanceDialog}
          onOpenChange={setShowAdvanceDialog}
          stats={stats}
          activeNations={activeNations || []}
          onConfirm={handleAdvancePhase}
          isAdvancing={isAdvancing}
        />
      )}
    </Layout>
  );
}
